import React from "react";
import { PropTypes } from "prop-types";

import basicFetch from "../../utils/basicFetch";
import playerLoader from "../../utils/playerLoader";

import style from "./PlayerShadowDom.module.scss";

/**
 * Testing version of Player2ShadowDom with additional controls for LTI Basic Outcomes.
 *
 * Usage :
 * ```js
 * <Player2ShadowDomTesting
 *   size='normal'
 *   playerOptions={playerOptions}
 * />
 * ```
 */

class Player2ShadowDomTesting extends React.Component {
  player = null;
  playerContainer = null;
  version = process.env.REACT_APP_PLAYER_2_VERSION;
  stage = process.env.REACT_APP_PLAYER_2_ENV;

  state = {
    testScoreValue: 0.85, // Default test score value
  }

  async componentDidMount() {
    const asset_manifest = await basicFetch(
      `https://dev.vivedlearning.com/v2/${this.stage}/${this.version}/asset-manifest.json`,
      {
        method: "get",
      }
    );

    const shadow = this.buildShadow();

    let entrypoints = asset_manifest.entrypoints.map((path) => {
      return `https://dev.vivedlearning.com/v2/${this.stage}/${this.version}/${path}`;
    });
    await playerLoader(entrypoints, shadow);

    this.startPlayer(shadow);
  }

  buildShadow() {
    let shadowParent = document.getElementById("player-2-shadow");
    let shadow = shadowParent.attachShadow({ mode: "open" });

    shadow.innerHTML = `
    <link type="text/css" href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">
    <style> .player-container{ display: flex; position: relative; height: 100%; width: 100%; } </style>
    <div id="player-container-authoring" class="player-container" />
    `;

    this.playerContainer = shadow.getElementById("player-container-authoring");

    shadow.createElement = (...args) => document.createElement(...args);
    shadow.createElementNS = (...args) => document.createElementNS(...args);
    shadow.createTextNode = (...args) => document.createTextNode(...args);

    return shadow;
  }

  startPlayer(shadow) {
    let options = {
      ...this.props.playerOptions,
      playerId: "9edccc93-bce4-40ed-a716-6b58294c8d99",
      secret:
        "IKIjmmhBqREJANHtlTmbQ6Y6GJ5kSszxe31Xu43LVQ3fRxz6AuMYc4zNPw8aUi16og1oa",
    };

    this.player = window.vived_player.mount(this.playerContainer, options);
  }

  componentWillUnmount() {
    if (this.player) {
      this.player.dispose();
    }

    if (this.playerContainer) {
      window.vived_player.unmount(this.playerContainer);
    }
  }

  handleTestScoreSubmit = () => {
    if (this.props.playerOptions?.postActivityScore) {
      console.log(`[DEV] Submitting test score: ${this.state.testScoreValue}`);
      this.props.playerOptions.postActivityScore(this.state.testScoreValue)
        .then(response => {
          console.log('[DEV] Score submission response:', response);
          alert(`Score ${this.state.testScoreValue} submitted successfully!`);
        })
        .catch(error => {
          console.error('[DEV] Score submission error:', error);
          alert(`Error submitting score: ${error.message}`);
        });
    } else {
      console.warn('[DEV] postActivityScore function not available');
      alert('postActivityScore function not available');
    }
  }

  handleTestScoreChange = (e) => {
    const value = parseFloat(e.target.value);
    this.setState({ testScoreValue: value });
  }

  renderDevTools() {
    if (!this.props.playerOptions?.postActivityScore) return null;
    
    const containerStyle = {
      position: 'absolute',
      top: '10px',
      right: '10px',
      zIndex: 1000,
      background: 'rgba(255, 255, 100, 0.9)',
      padding: '10px',
      border: '2px solid #ff6600',
      borderRadius: '5px',
      boxShadow: '0 0 10px rgba(0,0,0,0.5)',
    };
    
    return (
      <div style={containerStyle}>
        <div style={{marginBottom: '10px', fontWeight: 'bold'}}>LTI OUTCOME TEST</div>
        <div style={{display: 'flex', alignItems: 'center', marginBottom: '10px'}}>
          <label style={{marginRight: '10px'}}>Score (0-1):</label>
          <input 
            type="number" 
            min="0" 
            max="1" 
            step="0.01"
            value={this.state.testScoreValue}
            onChange={this.handleTestScoreChange}
            style={{width: '70px'}}
          />
        </div>
        <button 
          onClick={this.handleTestScoreSubmit}
          style={{
            padding: '5px 10px',
            background: '#ff6600',
            color: 'white',
            border: 'none',
            borderRadius: '3px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          Submit Score
        </button>
      </div>
    );
  }

  render() {
    const classes = [
      style.container,
      this.props.size === "normal" ? style.containerNormal : null,
      this.props.size === "freeplay" ? style.containerFreeplayFullsize : null,
      this.props.size === "authoring" ? style.containerAuthoring : null,
      this.props.size === "embed" ? style.containerEmbed : null,
      this.props.size === "fill" ? style.containerFill : null,
    ].join(" ");
    
    return (
      <div style={{ position: 'relative' }}>
        {this.renderDevTools()}
        <div id="player-2-shadow" className={classes} />
      </div>
    );
  }
}

Player2ShadowDomTesting.defaultProps = {
  appBar: false,
};

Player2ShadowDomTesting.propTypes = {
  appBar: PropTypes.bool,
  playerOptions: PropTypes.any,
  size: PropTypes.oneOf(["normal", "freeplay", "authoring", "embed", "fill"])
    .isRequired,
  title: PropTypes.string,
};

export default Player2ShadowDomTesting;
